import { Chip } from "@legal-500/legal500-design-system";
import { Link } from "react-router-dom";
import type { FirmLogoFillItem } from "../../../api/firmLogoFill.service.js";
import { buildFilesUrl } from "../../../api/filesUrl.js";
import { ROUTES } from "../../../routes.js";
import { kindColorOf, reasonTextOf } from "./outcomeLabels.js";
import { LogoThumbnail } from "./LogoThumbnail.js";
import { CandidateCard } from "./CandidateCard.js";
import { isPickableItem, logoSetIdOf } from "./logoPick.js";
import { LogoPicker } from "./LogoPicker.js";
import { locationLabel, websiteLabel } from "./rowLabels.js";
import styles from "./FirmLogoCopy.module.css";

const COLUMN_COUNT = 6;

type FirmLogoCopyRowProps = {
  item: FirmLogoFillItem;
  rowKey: string;
  isExpanded: boolean;
  canPickLogos: boolean;
  onToggle: () => void;
  onLogoSaved: () => Promise<void>;
};

export function FirmLogoCopyRow({
  item,
  rowKey,
  isExpanded,
  canPickLogos,
  onToggle,
  onLogoSaved,
}: FirmLogoCopyRowProps) {
  const firmName = item.companyName ?? "Unknown firm";
  const candidatesId = `firm-logo-candidates-${rowKey}`;
  const reasonText = reasonTextOf(item.reason);
  const isPickable = isPickableItem(item);
  const currentLogo = item.currentLogo;
  const currentLogoUrl = currentLogo
    ? buildFilesUrl({ hash: currentLogo.logoHash, filename: currentLogo.logoFilename })
    : null;

  return (
    <>
      <tr>
        <th scope="row">
          <Link className={styles.firmName} to={ROUTES.profiles.firmDetail(item.companyId)}>
            {firmName}
          </Link>
          <div className={styles.meta}>
            {item.companyWordpressId === null
              ? "No WordPress ID"
              : `WordPress ID ${item.companyWordpressId}`}
          </div>
        </th>
        <td>{websiteLabel(item.website)}</td>
        <td>{locationLabel(item)}</td>
        <td>
          <Chip size="small" color={kindColorOf(item.kind)}>
            {reasonText.label}
          </Chip>
          <div className={styles.meta}>{reasonText.explanation}</div>
        </td>
        <td>
          {isPickable && currentLogo ? (
            <span className={styles.logoSetLabel} id={logoSetIdOf(item)} tabIndex={-1}>
              <span aria-hidden="true">✓ </span>Logo set
            </span>
          ) : (
            <LogoThumbnail
              url={item.plannedLogoUrl}
              alt={`${firmName} planned logo`}
              key={item.plannedLogoUrl}
            />
          )}
          {isPickable ? (
            <button
              type="button"
              className={styles.toggle}
              aria-expanded={isExpanded}
              aria-controls={isExpanded ? candidatesId : undefined}
              onClick={onToggle}
            >
              {isExpanded ? "Hide" : "Show"} logos found ({item.candidates.length})
            </button>
          ) : null}
        </td>
        <td>
          <LogoThumbnail
            url={currentLogoUrl}
            alt={`${firmName} current logo`}
            key={currentLogo?.logoHash}
          />
        </td>
      </tr>
      {isPickable && isExpanded ? (
        <tr className={styles.expandedRow} id={candidatesId}>
          <td colSpan={COLUMN_COUNT}>
            {canPickLogos ? (
              <LogoPicker item={item} firmName={firmName} onSaved={onLogoSaved} />
            ) : (
              <ul className={styles.candidates} aria-label={`${firmName} logos found`}>
                {item.candidates.map((candidate, index) => (
                  <CandidateCard
                    key={candidate.logoHash}
                    candidate={candidate}
                    position={index + 1}
                    firmName={firmName}
                    isCurrent={currentLogo?.logoHash === candidate.logoHash}
                    selection="none"
                  />
                ))}
              </ul>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}
